import { BaseSyntheticEvent, useCallback, useState } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  setShowCreateRaindropForm,
  setShowCancelRaindropForm,
} from '@Modal/reducers/raindrops/createRaindropSlice';

// Form
import { useFormState } from 'react-hook-form';
import { useCreateRaindropForm } from '@Forms/raindrop/CreateRaindropFormContext';

// System
import { useSnackbar } from 'notistack';

// Types
import { AppDispatch } from 'src/app/store';
import { StoreInterface } from 'src/app/types';
import {
  useCreateRaindropCampaignMutation,
  RaindropCampaignsDocument,
} from '@Src/generated.graphql';

export const useCreateRaindrop = () => {
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [createRaindropCampaign] = useCreateRaindropCampaignMutation();
  const { showCancelRaindropForm, showCreateRaindropForm } = useSelector(
    (state: StoreInterface) => state?.createRaindrop
  );
  const { page, limit, raindropTableFilters } = useSelector(
    (state: StoreInterface) => state?.raindropTable
  );

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  // Handle Form Cancels
  const handleCreateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelRaindropForm(true));
    } else {
      return dispatch(setShowCreateRaindropForm(false));
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelRaindropForm(false));
      dispatch(setShowCreateRaindropForm(true));
    } else {
      reset();
      dispatch(setShowCancelRaindropForm(false));
      dispatch(setShowCreateRaindropForm(false));
    }
  };

  // Form Controls
  const { control, handleSubmit, reset } = useCreateRaindropForm();
  const { isDirty, errors } = useFormState({ control });

  const handleSubmitCreateRaindrop = useCallback(
    (e?: BaseSyntheticEvent) => {
      handleSubmit(async (formData) => {
        setIsSubmitting(true);

        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { showCasinoId, ...submitData } = formData;

          await createRaindropCampaign({
            variables: {
              // @ts-ignore
              input: {
                ...submitData,
                casinoId: showCasinoId ? submitData.casinoId : null,
              },
            },
            refetchQueries: [
              {
                query: RaindropCampaignsDocument,
                variables: {
                  pagination: {
                    page,
                    limit,
                  },
                  // @ts-ignore
                  filter: raindropTableFilters,
                },
              },
            ],
            awaitRefetchQueries: true,
          });

          enqueueSnackbar('Raindrop was successfully created', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          reset();
          dispatch(setShowCreateRaindropForm(false));
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          enqueueSnackbar(message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        } finally {
          setIsSubmitting(false);
        }
      })(e);
    },
    [createRaindropCampaign, page, limit, raindropTableFilters]
  );

  const handleOpenCreateRaindropModal = () => dispatch(setShowCreateRaindropForm(true));

  return {
    // Store
    showCancelRaindropForm,
    showCreateRaindropForm,
    submittable: isDirty && Object.values(errors).length == 0 && !isSubmitting,
    // Actions
    handleCreateCancel,
    handleCancelTakeMeBack,
    setShowCreateRaindropForm,
    handleSubmitCreateRaindrop,
    handleOpenCreateRaindropModal,
  };
};
